// src/api/vendedores.js - API para manejo de vendedores

import { apiFetch, API_CONFIG } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

// ==================== CONSTANTES ====================

// Estados posibles para vendedores
export const ESTADOS_VENDEDOR = ['activo', 'inactivo'];

// Comisión mínima y máxima por defecto
export const COMISION_MIN_DEFAULT = 0;
export const COMISION_MAX_DEFAULT = 100;

// ==================== FUNCIONES AUXILIARES (NO API) ====================

/**
 * Convertir de snake_case (API) a camelCase (frontend)
 * @param {Object} apiVendedor - Vendedor desde la API
 * @returns {Object} Vendedor convertido
 */
const convertirDesdeAPI = (apiVendedor) => {
  if (!apiVendedor) return null;
  
  // Si viene en un wrapper { data: { ... } }
  let source = apiVendedor;
  if (apiVendedor.data && !Array.isArray(apiVendedor.data) && typeof apiVendedor.data === 'object') {
    source = apiVendedor.data;
  }
  
  return {
    idVendedor: source.idVendedor || source.id || source.id_vendedor,
    idUsuario: source.idUsuario || source.id_usuario,
    nombre: toTitleCase(source.nombre || ''),
    apellido: toTitleCase(source.apellido || ''),
    descripcion: source.descripcion,
    comisionPorcentaje: source.comisionPorcentaje,
    fechaContratacion: source.fechaContratacion,
    estado: source.estado || 'activo',
    // Mantener también los campos originales
    ...apiVendedor,
    ...source,
    // Asegurar normalización
    idVendedor: source.idVendedor || source.id || apiVendedor.idVendedor || apiVendedor.id
  };
};

/**
 * Convertir de camelCase (frontend) a snake_case (API)
 * @param {Object} frontendVendedor - Vendedor desde el frontend
 * @returns {Object} Vendedor para la API
 */
const convertirParaAPI = (frontendVendedor) => {
  return {
    idUsuario: frontendVendedor.idUsuario,
    nombre: toTitleCase(frontendVendedor.nombre),
    apellido: toTitleCase(frontendVendedor.apellido || ''),
    descripcion: frontendVendedor.descripcion || '',
    comisionPorcentaje: frontendVendedor.comisionPorcentaje || 0,
    fechaContratacion: frontendVendedor.fechaContratacion || new Date().toISOString().split('T')[0],
    estado: frontendVendedor.estado || 'activo'
  };
};

/**
 * Convertir array de vendedores desde API
 * @param {Array} apiVendedores - Array de vendedores desde API
 * @returns {Array} Array de vendedores convertidos
 */
const convertirListaDesdeAPI = (apiVendedores) => {
  let lista = apiVendedores;
  if (apiVendedores && apiVendedores.data && Array.isArray(apiVendedores.data)) {
    lista = apiVendedores.data;
  }
  if (!Array.isArray(lista)) return [];
  return lista.map(convertirDesdeAPI);
};

/**
 * Formatear fecha de contratación
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFechaContratacion = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Convertir estado a texto legible
 * @param {string} estado - Estado del vendedor
 * @returns {string} Estado formateado
 */
export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1);
};

/**
 * Convertir estado a booleano
 * @param {string} estado - Estado como string
 * @returns {boolean} Estado booleano
 */
export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

/**
 * Formatear nombre completo del vendedor
 * @param {string} nombre - Nombre del vendedor
 * @param {string} apellido - Apellido del vendedor
 * @returns {string} Nombre completo
 */
export const formatearNombreCompleto = (nombre, apellido) => {
  if (!nombre && !apellido) return 'Sin nombre';
  const n = toTitleCase(nombre || '');
  const a = toTitleCase(apellido || '');
  return `${n} ${a}`.trim();
};

/**
 * Formatear comisión como porcentaje
 * @param {number} comision - Porcentaje de comisión
 * @returns {string} Comisión formateada
 */
export const formatearComision = (comision) => {
  if (comision === undefined || comision === null) return '0%';
  return `${comision}%`;
};

/**
 * Validar porcentaje de comisión
 * @param {number} comision - Porcentaje a validar
 * @returns {boolean} Comisión válida
 */
export const validarComision = (comision) => {
  if (comision === undefined || comision === null) return false;
  const num = Number(comision);
  return !isNaN(num) && num >= 0 && num <= 100;
};

/**
 * Validar fecha de contratación
 * @param {string} fecha - Fecha a validar (YYYY-MM-DD)
 * @returns {boolean} Fecha válida
 */
export const validarFechaContratacion = (fecha) => {
  if (!fecha) return true;
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(fecha)) return false;
  
  const fechaObj = new Date(fecha);
  return fechaObj instanceof Date && !isNaN(fechaObj);
};

/**
 * Buscar vendedores por término
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Vendedores filtrados
 */
export const buscarVendedores = (vendedores, termino) => {
  if (!termino || termino.trim() === '') {
    return vendedores;
  }
  
  const terminoLower = termino.toLowerCase();
  return vendedores.filter(vendedor => 
    vendedor.nombre?.toLowerCase().includes(terminoLower) ||
    vendedor.apellido?.toLowerCase().includes(terminoLower) ||
    formatearNombreCompleto(vendedor.nombre, vendedor.apellido).toLowerCase().includes(terminoLower) ||
    vendedor.descripcion?.toLowerCase().includes(terminoLower) ||
    convertirEstadoATexto(vendedor.estado)?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar vendedores por estado
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} estado - 'all', 'activos', 'inactivos'
 * @returns {Array} Vendedores filtrados
 */
export const filtrarVendedoresPorEstado = (vendedores, estado) => {
  if (estado === 'all') return vendedores;
  if (estado === 'activos') return vendedores.filter(v => v.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return vendedores.filter(v => v.estado?.toLowerCase() === 'inactivo');
  return vendedores;
};

/**
 * Filtrar vendedores por rango de comisión
 * @param {Array} vendedores - Lista de vendedores
 * @param {number} min - Comisión mínima
 * @param {number} max - Comisión máxima
 * @returns {Array} Vendedores filtrados
 */
export const filtrarVendedoresPorComision = (vendedores, min = 0, max = 100) => {
  return vendedores.filter(v => {
    const comision = v.comisionPorcentaje || 0;
    return comision >= min && comision <= max;
  });
};

/**
 * Obtener estadísticas de vendedores
 * @param {Array} vendedores - Lista de vendedores
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasVendedores = (vendedores) => {
  const activos = vendedores.filter(v => v.estado?.toLowerCase() === 'activo').length;
  const inactivos = vendedores.filter(v => v.estado?.toLowerCase() === 'inactivo').length;
  
  // Estadísticas de comisiones
  const comisiones = vendedores.map(v => v.comisionPorcentaje || 0);
  const comisionPromedio = comisiones.length > 0 
    ? comisiones.reduce((a, b) => a + b, 0) / comisiones.length 
    : 0;
  const comisionMaxima = comisiones.length > 0 ? Math.max(...comisiones) : 0;
  const comisionMinima = comisiones.length > 0 ? Math.min(...comisiones) : 0;
  
  // Vendedores por rango de comisión
  const comisionAlta = vendedores.filter(v => (v.comisionPorcentaje || 0) >= 10).length;
  const comisionMedia = vendedores.filter(v => (v.comisionPorcentaje || 0) >= 5 && (v.comisionPorcentaje || 0) < 10).length;
  const comisionBaja = vendedores.filter(v => (v.comisionPorcentaje || 0) < 5).length;
  
  // Vendedores con descripción
  const conDescripcion = vendedores.filter(v => v.descripcion && v.descripcion.trim() !== '').length;
  
  // Vendedores recientes (últimos 30 días)
  const treintaDiasAtras = new Date();
  treintaDiasAtras.setDate(treintaDiasAtras.getDate() - 30);
  const recientes = vendedores.filter(v => {
    try {
      const fechaContratacion = new Date(v.fechaContratacion || 0);
      return fechaContratacion >= treintaDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: vendedores.length,
    activos,
    inactivos,
    porcentajeActivos: vendedores.length > 0 ? Math.round((activos / vendedores.length) * 100) : 0,
    porcentajeInactivos: vendedores.length > 0 ? Math.round((inactivos / vendedores.length) * 100) : 0,
    comisionPromedio: Math.round(comisionPromedio * 100) / 100,
    comisionMaxima,
    comisionMinima,
    comisionAlta,
    comisionMedia,
    comisionBaja,
    conDescripcion,
    porcentajeConDescripcion: vendedores.length > 0 ? Math.round((conDescripcion / vendedores.length) * 100) : 0,
    recientes,
    porcentajeRecientes: vendedores.length > 0 ? Math.round((recientes / vendedores.length) * 100) : 0
  };
};

/**
 * Ordenar vendedores por diferentes criterios
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Vendedores ordenados
 */
export const ordenarVendedores = (vendedores, criterio = 'nombre') => {
  const vendedoresCopy = [...vendedores];
  
  switch (criterio) {
    case 'nombre':
      return vendedoresCopy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return vendedoresCopy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'apellido':
      return vendedoresCopy.sort((a, b) => (a.apellido || '').localeCompare(b.apellido || ''));
    case 'comision_desc':
      return vendedoresCopy.sort((a, b) => (b.comisionPorcentaje || 0) - (a.comisionPorcentaje || 0));
    case 'comision':
      return vendedoresCopy.sort((a, b) => (a.comisionPorcentaje || 0) - (b.comisionPorcentaje || 0));
    case 'fechaContratacion_desc':
      return vendedoresCopy.sort((a, b) => new Date(b.fechaContratacion || 0) - new Date(a.fechaContratacion || 0));
    case 'fechaContratacion':
      return vendedoresCopy.sort((a, b) => new Date(a.fechaContratacion || 0) - new Date(b.fechaContratacion || 0));
    case 'estado':
      return vendedoresCopy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    default:
      return vendedoresCopy;
  }
};

// ==================== FUNCIONES API CORREGIDAS ====================

/**
 * Obtener todos los vendedores
 * @returns {Promise} Lista de vendedores
 */
export const obtenerVendedores = async () => {
  try {
    console.log('📋 Obteniendo vendedores...');
    
    const response = await apiFetch('/Vendedor');
    
    const vendedores = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: vendedores,
      count: vendedores.length,
      message: 'Vendedores obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo vendedores:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener ranking de vendedores
 * @returns {Promise} Ranking de vendedores
 */
export const obtenerRankingVendedores = async () => {
  try {
    console.log('🏆 Obteniendo ranking de vendedores...');
    
    const response = await apiFetch('/Vendedor/ranking');
    
    const ranking = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: ranking,
      count: ranking.length,
      message: 'Ranking de vendedores obtenido exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo ranking de vendedores:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener vendedores por rango de comisión
 * @param {number} min - Comisión mínima
 * @param {number} max - Comisión máxima
 * @returns {Promise} Vendedores en el rango
 */
export const obtenerVendedoresPorRangoComision = async (min = 0, max = 100) => {
  try {
    if (min === undefined || max === undefined) {
      throw new Error('Los valores mínimo y máximo son requeridos');
    }
    
    console.log(`📋 Obteniendo vendedores con comisión entre ${min}% y ${max}%...`);
    
    const response = await apiFetch(`/Vendedor/comision/${min}/${max}`);
    
    const vendedores = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: vendedores,
      count: vendedores.length,
      min,
      max,
      message: `Vendedores con comisión entre ${min}% y ${max}% obtenidos exitosamente`
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo vendedores por rango de comisión:`, error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0,
      min,
      max
    };
  }
};

/**
 * Obtener vendedor por ID de usuario
 * @param {number} idUsuario - ID del usuario
 * @returns {Promise} Vendedor encontrado
 */
export const obtenerVendedorPorIdUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`🔍 Obteniendo vendedor por ID de usuario: ${idUsuario}`);
    
    const response = await apiFetch(`/Vendedor/usuario/${idUsuario}`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Vendedor obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo vendedor por ID de usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener un vendedor por ID
 * @param {number} id - ID del vendedor
 * @returns {Promise} Datos del vendedor
 */
export const obtenerVendedorPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vendedor es requerido');
    }
    
    console.log(`🔍 Obteniendo vendedor ID: ${id}`);
    
    const response = await apiFetch(`/Vendedor/${id}`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Vendedor obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo vendedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener vendedor con relaciones
 * @param {number} id - ID del vendedor
 * @returns {Promise} Vendedor con relaciones
 */
export const obtenerVendedorConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vendedor es requerido');
    }
    
    console.log(`🔍 Obteniendo vendedor ID: ${id} con relaciones`);
    
    const response = await apiFetch(`/Vendedor/${id}/conRelaciones`);
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Vendedor con relaciones obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo vendedor ${id} con relaciones:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener estadísticas de un vendedor
 * @param {number} id - ID del vendedor
 * @returns {Promise} Estadísticas del vendedor
 */
export const obtenerEstadisticasVendedor = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vendedor es requerido');
    }
    
    console.log(`📊 Obteniendo estadísticas del vendedor ID: ${id}`);
    
    const response = await apiFetch(`/Vendedor/${id}/estadisticas`);
    
    return {
      success: true,
      data: response,
      message: 'Estadísticas del vendedor obtenidas exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estadísticas del vendedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Buscar vendedores por término (usa el endpoint search)
 * @param {string} termino - Término de búsqueda
 * @returns {Promise} Vendedores encontrados
 */
export const buscarVendedoresPorTermino = async (termino) => {
  try {
    if (!termino || termino.trim() === '') {
      return {
        success: true,
        data: [],
        count: 0,
        message: 'Ingrese un término de búsqueda'
      };
    }
    
    console.log(`🔍 Buscando vendedores por: "${termino}"`);
    
    const response = await apiFetch('/Vendedor/search', {
      method: 'POST',
      body: JSON.stringify({ termino: termino.trim() })
    });
    
    const vendedores = convertirListaDesdeAPI(response);
    
    return {
      success: true,
      data: vendedores,
      count: vendedores.length,
      message: 'Búsqueda completada'
    };
    
  } catch (error) {
    console.error('❌ Error buscando vendedores:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Verificar si un vendedor existe por ID
 * @param {number} id - ID a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarVendedorPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID es requerido');
    }
    
    console.log(`🔎 Verificando vendedor por ID: ${id}`);
    
    const response = await apiFetch(`/Vendedor/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || false,
      message: response.exists ? 'El ID existe' : 'ID disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando vendedor por ID ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si existe un vendedor por ID de usuario
 * @param {number} idUsuario - ID de usuario a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarVendedorPorIdUsuario = async (idUsuario) => {
  try {
    if (!idUsuario) {
      throw new Error('ID de usuario es requerido');
    }
    
    console.log(`🔎 Verificando vendedor por ID de usuario: ${idUsuario}`);
    
    const response = await apiFetch(`/Vendedor/usuario/${idUsuario}/exists`);
    
    return {
      success: true,
      existe: response.exists || false,
      message: response.exists ? 'El usuario ya está registrado como vendedor' : 'Usuario disponible'
    };
    
  } catch (error) {
    console.error(`❌ Error verificando vendedor por ID de usuario ${idUsuario}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo vendedor
 * @param {Object} vendedorData - Datos del vendedor
 * @returns {Promise} Vendedor creado
 */
export const crearVendedor = async (vendedorData) => {
  try {
    // Validar datos requeridos
    if (!vendedorData.idUsuario) {
      throw new Error('El ID de usuario es requerido');
    }
    
    if (!vendedorData.nombre || vendedorData.nombre.trim() === '') {
      throw new Error('El nombre del vendedor es requerido');
    }
    
    if (vendedorData.comisionPorcentaje !== undefined && !validarComision(vendedorData.comisionPorcentaje)) {
      throw new Error('El porcentaje de comisión debe estar entre 0 y 100');
    }
    
    console.log('➕ Creando nuevo vendedor:', vendedorData);
    
    // Convertir a formato API
    const apiData = convertirParaAPI(vendedorData);
    
    const response = await apiFetch('/Vendedor', {
      method: 'POST',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Vendedor creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando vendedor:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un vendedor existente
 * @param {number} id - ID del vendedor
 * @param {Object} vendedorData - Datos a actualizar
 * @returns {Promise} Vendedor actualizado
 */
export const actualizarVendedor = async (id, vendedorData) => {
  try {
    if (!id) {
      throw new Error('ID de vendedor es requerido');
    }
    
    if (!vendedorData.idUsuario) {
      throw new Error('El ID de usuario es requerido');
    }
    
    if (!vendedorData.nombre || vendedorData.nombre.trim() === '') {
      throw new Error('El nombre del vendedor es requerido');
    }
    
    if (vendedorData.comisionPorcentaje !== undefined && !validarComision(vendedorData.comisionPorcentaje)) {
      throw new Error('El porcentaje de comisión debe estar entre 0 y 100');
    }
    
    console.log(`✏️ Actualizando vendedor ID: ${id}`, vendedorData);
    
    // Convertir a formato API
    const apiData = convertirParaAPI(vendedorData);
    
    const response = await apiFetch(`/Vendedor/${id}`, {
      method: 'PUT',
      body: JSON.stringify(apiData)
    });
    
    return {
      success: true,
      data: convertirDesdeAPI(response),
      message: 'Vendedor actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando vendedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un vendedor
 * @param {number} id - ID del vendedor a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarVendedor = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de vendedor es requerido');
    }
    
    console.log(`🗑️ Eliminando vendedor ID: ${id}`);
    
    const response = await apiFetch(`/Vendedor/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response,
      message: response.message || 'Vendedor eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando vendedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado de un vendedor
 * @param {number} id - ID del vendedor
 * @param {string} estado - Nuevo estado
 * @returns {Promise} Resultado del cambio de estado
 */
export const cambiarEstadoVendedor = async (id, estado) => {
  try {
    if (!id || !estado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado de vendedor ID: ${id} a ${estado}`);
    
    // Primero obtenemos el vendedor
    const vendedorResult = await obtenerVendedorPorId(id);
    
    if (!vendedorResult.success || !vendedorResult.data) {
      throw new Error('No se pudo obtener el vendedor');
    }
    
    // Actualizamos el estado
    const vendedor = vendedorResult.data;
    vendedor.estado = estado;
    
    // Usamos PUT para actualizar
    return await actualizarVendedor(id, vendedor);
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de vendedor ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar un vendedor
 * @param {number} id - ID del vendedor
 * @returns {Promise} Resultado de la activación
 */
export const activarVendedor = async (id) => {
  return await cambiarEstadoVendedor(id, 'activo');
};

/**
 * Desactivar un vendedor
 * @param {number} id - ID del vendedor
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarVendedor = async (id) => {
  return await cambiarEstadoVendedor(id, 'inactivo');
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Función de exportación a PDF
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (OPCIONAL)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (vendedores, titulo = 'Reporte de Vendedores', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de vendedores...');
    console.log('📈 Vendedores a exportar:', vendedores.length);
    
    // Verificar que estamos en el navegador
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!vendedores || vendedores.length === 0) {
      throw new Error('No hay vendedores para exportar');
    }
    
    // Usar jsPDF
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible.',
        filename: null
      };
    }
    
    const doc = new jsPDF({ orientation: 'landscape' });
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const horaStr = fecha.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let y = 20;
    const marginLeft = 20;
    
    // Agregar logo si se proporcionó
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        console.log('✅ Logo agregado al PDF');
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    // Título
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('JEAN INFORMATICO C.A.', titleX, y);
    
    // Subtítulo
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    // Información de fecha y total
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Hora: ${horaStr}`, marginLeft + 50, y);
    doc.text(`Total: ${vendedores.length} vendedores`, marginLeft + 100, y);
    
    // Encabezados de tabla
    y += 15;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    
    const colPositions = {
      id: marginLeft,
      nombre: marginLeft + 15,
      comision: marginLeft + 65,
      fecha: marginLeft + 85,
      estado: marginLeft + 120
    };
    
    doc.text('ID', colPositions.id, y);
    doc.text('Nombre', colPositions.nombre, y);
    doc.text('Comisión', colPositions.comision, y);
    doc.text('Fecha Contratación', colPositions.fecha, y);
    doc.text('Estado', colPositions.estado, y);
    
    // Línea separadora
    y += 5;
    doc.setDrawColor(200, 200, 200);
    doc.line(marginLeft, y, marginLeft + 170, y);
    
    // Datos de los vendedores
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    vendedores.forEach((vendedor, index) => {
      if (y > 190) {
        doc.addPage();
        y = 30;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('ID', colPositions.id, 20);
        doc.text('Nombre', colPositions.nombre, 20);
        doc.text('Comisión', colPositions.comision, 20);
        doc.text('Fecha Contratación', colPositions.fecha, 20);
        doc.text('Estado', colPositions.estado, 20);
        y += 15;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
      }
      
      const idStr = String(vendedor.idVendedor || index + 1);
      const nombreStr = String(formatearNombreCompleto(vendedor.nombre, vendedor.apellido) || 'N/A');
      const comisionStr = formatearComision(vendedor.comisionPorcentaje);
      const fechaStrVendedor = formatearFechaContratacion(vendedor.fechaContratacion);
      const estadoStr = convertirEstadoATexto(vendedor.estado);
      
      // Truncar textos largos
      const truncarTexto = (texto, maxLength = 20) => {
        if (texto.length > maxLength) {
          return texto.substring(0, maxLength - 3) + '...';
        }
        return texto;
      };
      
      doc.text(truncarTexto(idStr, 10), colPositions.id, y);
      doc.text(truncarTexto(nombreStr, 25), colPositions.nombre, y);
      
      // Color según comisión
      const comision = vendedor.comisionPorcentaje || 0;
      if (comision >= 10) {
        doc.setTextColor(0, 128, 0);
      } else if (comision >= 5) {
        doc.setTextColor(255, 140, 0);
      } else {
        doc.setTextColor(128, 128, 128);
      }
      doc.text(comisionStr, colPositions.comision, y);
      doc.setTextColor(0, 0, 0);
      
      doc.text(truncarTexto(fechaStrVendedor, 15), colPositions.fecha, y);
      
      // Color según estado
      const estadoLower = estadoStr.toLowerCase();
      if (estadoLower === 'activo') {
        doc.setTextColor(0, 128, 0);
      } else if (estadoLower === 'inactivo') {
        doc.setTextColor(255, 0, 0);
      } else {
        doc.setTextColor(128, 128, 128);
      }
      
      doc.text(truncarTexto(estadoStr, 10), colPositions.estado, y);
      doc.setTextColor(0, 0, 0);
      
      y += 6;
    });
    
    // Pie de página
    const pageWidth = doc.internal.pageSize.width;
    const pageCount = doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.text('© Jean Informatico C.A. - Sistema de Gestión de Vendedores', marginLeft, 200);
      doc.text(`Página ${i} de ${pageCount} - ${fechaStr}`, pageWidth - marginLeft, 200, null, null, 'right');
    }
    
    // Guardar archivo
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `vendedores_${fechaISO}.pdf`;
    
    doc.save(nombreArchivo);
    
    console.log('✅ PDF generado exitosamente:', nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: logoAgregado 
        ? 'PDF generado exitosamente con logo' 
        : 'PDF generado exitosamente',
      count: vendedores.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar vendedores a Excel
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (vendedores, filename = 'vendedores') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!vendedores || vendedores.length === 0) {
      throw new Error('No hay vendedores para exportar');
    }
    
    const XLSX = require('xlsx');
    
    // Preparar datos
    const data = vendedores.map((vendedor, index) => ({
      '#': index + 1,
      'ID': vendedor.idVendedor || '',
      'ID Usuario': vendedor.idUsuario || '',
      'Nombre': vendedor.nombre || '',
      'Apellido': vendedor.apellido || '',
      'Nombre Completo': formatearNombreCompleto(vendedor.nombre, vendedor.apellido),
      'Comisión (%)': vendedor.comisionPorcentaje || 0,
      'Fecha Contratación': formatearFechaContratacion(vendedor.fechaContratacion),
      'Descripción': vendedor.descripcion || '',
      'Estado': convertirEstadoATexto(vendedor.estado)
    }));
    
    // Crear hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(data);
    
    // Ajustar anchos de columna
    const wscols = [
      { wch: 5 },   // #
      { wch: 8 },   // ID
      { wch: 12 },  // ID Usuario
      { wch: 20 },  // Nombre
      { wch: 20 },  // Apellido
      { wch: 25 },  // Nombre Completo
      { wch: 12 },  // Comisión (%)
      { wch: 20 },  // Fecha Contratación
      { wch: 40 },  // Descripción
      { wch: 12 }   // Estado
    ];
    ws['!cols'] = wscols;
    
    // Crear libro
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendedores');
    
    // Generar nombre de archivo
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    // Guardar
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'Archivo Excel generado exitosamente',
      count: vendedores.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo de vendedores
 * @param {Array} vendedores - Lista de vendedores
 * @param {string} baseFilename - Nombre base
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado
 */
export const exportarReporteCompleto = async (vendedores, baseFilename = 'reporte_completo_vendedores', logoBase64 = null) => {
  try {
    const excelResult = exportarExcel(vendedores, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const pdfResult = exportarPDF(vendedores, 'Reporte de Vendedores - Jean Informatico C.A.', logoBase64);
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: vendedores.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Constantes
  ESTADOS_VENDEDOR,
  COMISION_MIN_DEFAULT,
  COMISION_MAX_DEFAULT,
  
  // Funciones API
  obtenerVendedores,
  obtenerRankingVendedores,
  obtenerVendedoresPorRangoComision,
  obtenerVendedorPorIdUsuario,
  obtenerVendedorPorId,
  obtenerVendedorConRelaciones,
  obtenerEstadisticasVendedor,
  buscarVendedoresPorTermino,
  verificarVendedorPorId,
  verificarVendedorPorIdUsuario,
  crearVendedor,
  actualizarVendedor,
  eliminarVendedor,
  activarVendedor,
  desactivarVendedor,
  cambiarEstadoVendedor,
  
  // Funciones auxiliares
  formatearFechaContratacion,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  formatearNombreCompleto,
  formatearComision,
  validarComision,
  validarFechaContratacion,
  buscarVendedores,
  filtrarVendedoresPorEstado,
  filtrarVendedoresPorComision,
  obtenerEstadisticasVendedores,
  ordenarVendedores,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto,
  
  // Funciones de conversión (por si se necesitan)
  convertirDesdeAPI,
  convertirParaAPI
};